import React, { useState, useEffect } from 'react'
import { Upload, UploadFile, UploadProps, message, Select } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { useAppSelector } from '../../../../store/hooks'
import { getMerchantById, updateMerchantInfo } from '../../../../api/user'
import styles from './StoreInformation.module.scss'
import { useTranslation } from 'react-i18next'

const { Option } = Select;

const StoreInformation = () => {
  const [storeName, setStoreName] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [address, setAddress] = useState('')
  const [aptSuiteBuilding, setAptSuiteBuilding] = useState('')
  const [zipCode, setZipCode] = useState('')
  const [cityOrTownship, setCityOrTownship] = useState('')
  const [state, setState] = useState('') // 设置默认值为Alabama
  const [isEditing, setIsEditing] = useState(false)
  const [logoFileList, setLogoFileList] = useState<UploadFile[]>([])
  const [logoUrl, setLogoUrl] = useState('http://test.townloop.app:8002/api/web/files/uploads/images/551acda3-8a03-41d5-ad54-fcd8f36131a6.png')
  const [merchantInfo, setMerchantInfo] = useState<any>(null)
  const [loading, setLoading] = useState(false)

  // 获取token和用户信息
  const token = useAppSelector((state) => state.user.token)
  const userInfo = useAppSelector((state) => state.user.userInfo)

    //国际化语言切换
    const { t, i18n } = useTranslation();
  // 获取merchant_id，如果没有则显示默认值
  const merchantId = userInfo?.merchant_id || ''

  // 美国州列表
  const StateList = useAppSelector((state) => state.common.StateList)

  console.log('StateList:', StateList)
  console.log('StateList length:', StateList?.length)

  // 获取商户信息
  useEffect(() => {
    const fetchMerchantInfo = async () => {
      if (!merchantId) {
        console.log('No merchant_id available')
        return
      }

      setLoading(true)
      try {
        const response = await getMerchantById({ merchant_id: merchantId })
        console.log('Merchant info response:', response)
        
        if (response.code === 200 && response.data) {
          setMerchantInfo(response.data)
          // 根据实际API返回的数据结构更新字段
          if (response.data.merchant_name) {
            setStoreName(response.data.merchant_name)
          }
          if (response.data.first_name) {
            setFirstName(response.data.first_name)
          }
          if (response.data.last_name) {
            setLastName(response.data.last_name)
          }
          if (response.data.phone) {
            setPhone(response.data.phone)
          }
          if (response.data.email) {
            setEmail(response.data.email)
          }
          if (response.data.address) {
            setAddress(response.data.address)
          }
          if (response.data.building) {
            setAptSuiteBuilding(response.data.building)
          }
          if (response.data.zip_code) {
            setZipCode(response.data.zip_code)
          }
          if (response.data.city) {
            setCityOrTownship(response.data.city)
          }
          if (response.data.state) {
            setState(response.data.state)
          }
          if (response.data.store_logo) {
            setLogoUrl(response.data.store_logo?.[0]?.url || '');
            setLogoFileList(response.data.store_logo || []);
          }
        }
      } catch (error) {
      } finally {
        setLoading(false)
      }
    }

    fetchMerchantInfo()
  }, [merchantId])

  // 图片上传配置
  const logoUploadProps: UploadProps = {
    name: 'file',
    action: `${process.env.REACT_APP_API_URL}/api/product/save_upload_file`,
    accept: ".png,.jpeg,.jpg,.gif",
    headers: {
      token: token || '',
    },
    listType: 'picture-card',
    maxCount: 1,
    showUploadList: false,
    fileList: logoFileList,
    beforeUpload: (file) => {
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error("Image must be smaller than 10MB!");
      }
      return isLt10M; // 返回 false 阻止上传
    },
    onChange(info) {
      console.log('Upload onChange:', info)
      console.log('File status:', info.file.status)
      console.log('File response:', info.file.response)
      
      // 复制一份文件列表
      const newFileList = [...info.fileList];

      // 对文件状态进行处理
      newFileList.forEach(file => {
        if (file.response) {
          // 可以从响应中获取文件URL
          file.url = file?.response?.data?.url;
        }
      });

      // 更新文件列表状态
      setLogoFileList(newFileList);
      
      if (info.file.status === 'done') {
        console.log('Upload done, response:', info.file.response)
        console.log('Response data:', info.file.response?.data)
        
        if (info.file.response?.data?.url) {
          const newLogoUrl = info.file.response.data.url
          console.log('Setting new logo URL:', newLogoUrl)
          setLogoUrl(newLogoUrl)
          message.success('Logo uploaded successfully')
        } else {
          console.log('No URL in response')
          message.error('Upload response does not contain URL')
        }
      } else if (info.file.status === 'error') {
        console.log('Upload error:', info.file.error)
        message.error(`${info.file.name} upload failed`)
      } else if (info.file.status === 'uploading') {
        console.log('Upload in progress...')
      }
    },
  }

  const handleCopySellerId = () => {
    navigator.clipboard.writeText(merchantId)
  }

  const handleEdit = () => {
    setIsEditing(true)
  }

  const handleCancel = () => {
    setIsEditing(false)
    // 重置logo文件列表
    setLogoFileList([])
  }

  const handleSave = async () => {
    if (!merchantId) {
      message.error('No merchant ID available')
      return
    }

    setLoading(true)
    try {
      // 调用更新商户信息的API
      const updateData = {
        ...merchantInfo,
        merchant_id: merchantId,
        merchant_name: storeName,
        first_name: firstName,
        last_name: lastName,
        phone: phone,
        address: address,
        building: aptSuiteBuilding,
        zip_code: zipCode,
        city: cityOrTownship,
        state: state,
        store_logo: logoFileList.map(file => ({
          uid: file.uid,
          url: file.url,
        })),
        email:email,
      }
      
      const response = await updateMerchantInfo(updateData)
      
      if (response.code === 200) {
        setIsEditing(false)
        // 清空文件列表
        setLogoFileList([])
        message.success('Store information saved successfully')
      } else {
        message.error(response.message || 'Failed to save store information')
      }
    } catch (error) {
      console.error('Failed to save merchant info:', error)
      message.error('Failed to save store information')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={styles.container}>
      {/* {JSON.stringify(merchantInfo)} */}
      {/* {JSON.stringify(logoFileList)} */}
      {/* 编辑按钮 */}
      <button className={styles.editButton} onClick={handleEdit} disabled={loading}>
        {loading ? t('storeInformation.Loading') : t('storeInformation.Edit')}
      </button>

      {/* 店铺信息部分 */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('storeInformation.StoreInformation')}</h2>
        
        {loading && (
          <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
            {t('storeInformation.Loadingmerchantinformation')}...
          </div>
        )}

        <div className={styles.formGroup}>
          <label className={styles.label}>{t('storeInformation.StoreName')}</label>
          <div className={styles.inputWrapper}>
            <input
              type="text"
              placeholder="Store Name"
              value={storeName}
              onChange={(e) => setStoreName(e.target.value)}
              className={styles.input}
              maxLength={20}
              disabled={!isEditing}
            />
            <div className={styles.charCount}>
              {storeName.length}/20
              <span className={styles.checkIcon}>✓</span>
            </div>
          </div>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>{t('storeInformation.SellerID')}</label>
          <div className={styles.sellerIdWrapper}>
            <div className={styles.sellerIdDisplay}>{merchantId}</div>
            <button className={styles.copyButton} onClick={handleCopySellerId}>
            {t('storeInformation.Copy')}
            </button>
          </div>
        </div>
        <div className={styles.formGroup}>
          <label className={styles.label}>{t('storeInformation.StoreLogo')}</label>
          <div className={styles.logoUpload}>
            <div className={styles.logoIcon}>
              <img src={logoUrl} alt="Store Logo" />
            </div>
            {isEditing && (
              <Upload {...logoUploadProps}>
                <div className={styles.uploadOverlay}>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>{t('storeInformation.Change')}</div>
                </div>
              </Upload>
            )}
          </div>
        </div>
      </div>

      {/* 分割线 */}
      <div className={styles.divider}></div>

      {/* 联系地址部分 */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('storeInformation.ContactAddress')}</h2>
        
        <div className={styles.addressGrid}>
          <div className={styles.formGroup}>
            <label className={styles.label}>
            {t('storeInformation.FristName')}
            </label>
            <input
              type="text"
              placeholder="Frist Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className={styles.addressInput}
              disabled={!isEditing}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>
            {t('storeInformation.LastName')}
            </label>
            <input
              type="text"
              placeholder="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className={styles.addressInput}
              disabled={!isEditing}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>
            {t('storeInformation.Phone')}
            </label>
            <input
              type="tel"
              placeholder="Phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className={styles.addressInput}
              disabled={!isEditing}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>
            {t('storeInformation.Email')}
            </label>
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={styles.addressInput}
              disabled={!isEditing}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>
            {t('storeInformation.Address')}
            </label>
            <input
              type="text"
              placeholder="Address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              className={styles.addressInput}
              disabled={!isEditing}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>
            {t('storeInformation.AptSuiteBuilding')}
             </label> 
            <input
              type="text"
              placeholder="Apt,Suite,Building"
              value={aptSuiteBuilding}
              onChange={(e) => setAptSuiteBuilding(e.target.value)}
              className={styles.addressInput}
              disabled={!isEditing}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>
            {t('storeInformation.ZipCode')}
            </label>
            <input
              type="text"
              placeholder="Zip Code"
              value={zipCode}
              onChange={(e) => setZipCode(e.target.value)}
              className={styles.addressInput}
              disabled={!isEditing}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>
            {t('storeInformation.CityOrTownship')}
            </label>
            <input
              type="text"
              placeholder="City Or Township"
              value={cityOrTownship}
              onChange={(e) => setCityOrTownship(e.target.value)}
              className={styles.addressInput}
              disabled={!isEditing}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>
            {t('storeInformation.State')}
            </label>
            <Select
              style={{ width: '100%' }}
              value={state}
              onChange={(value) => setState(value)}
              disabled={!isEditing}
              placeholder="Please select a state"
            >
              {StateList.map((item, index) => (
                <Option key={index} value={item.value}>
                  {item.label}
                </Option>
              ))}
            </Select>
          </div>
        </div>

        {/* 保存和取消按钮 */}
        {isEditing && (
          <div className={styles.buttonGroup}>
            <button className={styles.cancelButton} onClick={handleCancel} disabled={loading}>
                  {t('storeInformation.Cancel')}
            </button>
            <button className={styles.saveButton} onClick={handleSave} disabled={loading}>
              {loading ? t('storeInformation.Saving') : t('storeInformation.Save')}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default StoreInformation